import React, { useState } from 'react';
import { X, Search, Download, ExternalLink } from 'lucide-react';

interface StockImage {
  id: string;
  url: string;
  thumbnail: string;
  photographer: string;
  photographerUrl: string;
  width: number;
  height: number;
  alt: string;
  source: 'pexels' | 'freepik';
}

interface StockImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (image: StockImage) => void;
  initialQuery?: string;
  tags?: string[];
}

const StockImageModal: React.FC<StockImageModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  initialQuery = '',
  tags = []
}) => {
  const [query, setQuery] = useState(initialQuery);
  const [searching, setSearching] = useState(false);
  const [images, setImages] = useState<StockImage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedSource, setSelectedSource] = useState<'pexels' | 'freepik'>('pexels');



  const handleSearch = async () => {
    if (!query.trim()) return;
    
    setSearching(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/images/stock/search?query=${encodeURIComponent(query)}&source=${selectedSource}&perPage=20`);
      
      if (!response.ok) {
        throw new Error('Error al buscar imágenes');
      }
      
      const data = await response.json();
      setImages(data.images || []);
      
      if (data.images && data.images.length === 0) {
        setError('No se encontraron imágenes para tu búsqueda');
      }
    } catch (err) {
      console.error('Error searching images:', err);
      setError('Error al buscar imágenes. Intenta de nuevo.');
    } finally {
      setSearching(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSelect = (image: StockImage) => {
    onSelect(image);
    onClose();
  };

  const handleSourceChange = (source: 'pexels' | 'freepik') => {
    setSelectedSource(source);
    setImages([]); // Clear current images when changing source
  };

  // Generate search suggestions from tags
  const getSearchSuggestions = () => {
    if (tags.length === 0) return [];
    
    return tags.map(tag => ({
      text: tag,
      onClick: () => {
        setQuery(tag);
        handleSearch();
      }
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background border border-border rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-foreground">Buscar Imágenes de Stock</h2>
          <button
            onClick={onClose}
            className="p-1 text-muted-foreground hover:text-foreground hover:bg-surface-200 rounded transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Source Selection */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => handleSourceChange('pexels')}
            className={`px-4 py-2 rounded-md transition-colors ${
              selectedSource === 'pexels' 
                ? 'bg-orange-500 text-white' 
                : 'bg-surface-200 text-foreground hover:bg-surface-300'
            }`}
          >
            Pexels
          </button>
          <button
            onClick={() => handleSourceChange('freepik')}
            className={`px-4 py-2 rounded-md transition-colors ${
              selectedSource === 'freepik' 
                ? 'bg-blue-500 text-white' 
                : 'bg-surface-200 text-foreground hover:bg-surface-300'
            }`}
          >
            Freepik
          </button>
        </div>

        {/* Search Bar */}
        <div className="flex gap-3 mb-4">
          <div className="flex-1 relative">
            <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Buscar imágenes (ej: estadísticas, datos, gráficos)..."
              className="w-full pl-10 pr-4 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={!query.trim() || searching}
            className="px-6 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {searching ? 'Buscando...' : 'Buscar'}
          </button>
        </div>

        {/* Tags Suggestions */}
        {tags.length > 0 && (
          <div className="mb-4">
            <p className="text-sm text-muted-foreground mb-2">Sugerencias basadas en los tags:</p>
            <div className="flex flex-wrap gap-2">
              {getSearchSuggestions().map((suggestion, index) => (
                <button
                  key={index}
                  onClick={suggestion.onClick}
                  className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm hover:bg-primary/20 transition-colors"
                >
                  {suggestion.text}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Images Grid */}
        <div className="flex-1 overflow-y-auto">
          {images.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {images.map((image) => (
                <div
                  key={image.id}
                  className="group relative bg-surface-100 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <img
                    src={image.thumbnail}
                    alt={image.alt}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                      <button
                        onClick={() => handleSelect(image)}
                        className="px-3 py-1 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90 transition-colors"
                      >
                        Seleccionar
                      </button>
                      <a
                        href={image.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1 bg-surface-200 text-foreground rounded-md text-sm hover:bg-surface-300 transition-colors flex items-center gap-1"
                      >
                        <ExternalLink size={14} />
                        Ver
                      </a>
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs px-2 py-1 rounded ${
                        image.source === 'pexels' 
                          ? 'bg-orange-100 text-orange-700' 
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {image.source}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground truncate">
                      Por{' '}
                      <a
                        href={image.photographerUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {image.photographer}
                      </a>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {image.width} × {image.height}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Search size={48} className="mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {searching ? 'Buscando imágenes...' : 'Busca imágenes para comenzar'}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-border">
          <p className="text-xs text-muted-foreground text-center">
            Imágenes proporcionadas por{' '}
            <a
              href="https://www.pexels.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-orange-500 hover:underline"
            >
              Pexels
            </a>
            {' '}y{' '}
            <a
              href="https://www.freepik.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:underline"
            >
              Freepik
            </a>
            . Todas las imágenes son gratuitas para uso comercial.
          </p>
        </div>
      </div>
    </div>
  );
};

export default StockImageModal; 